import React, { useState } from 'react'
import { Box, Typography, Button, Container, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import {
  Radio,
  Zap,
  Users,
  Mic,
  MessageCircle,
  TrendingUp,
  Search,
  ArrowRight,
  CheckCircle,
  Star,
  BarChart3,
  Link2,
  ChevronDown,
} from 'lucide-react'
import {
  HeroMock,
  DiscoverMock,
  EngageMock,
  NetworkMock,
  PromoteMock,
  AnalyzeMock,
  StepOneMock,
  StepTwoMock,
  StepThreeMock,
} from './MockElements'

// ==========================================
// FEATURE TABS
// ==========================================
const FEATURE_TABS = [
  {
    id: 'discover',
    label: 'Discover',
    icon: <Search size={18} />,
    title: 'Find every conversation about your podcast topics',
    points: [
      'Social Radar scans Instagram, TikTok, YouTube, Reddit, and Twitter daily',
      'AI extracts talking points from your latest episodes automatically',
      'Mentions signal catches people recommending your show',
    ],
  },
  {
    id: 'engage',
    label: 'Engage',
    icon: <Zap size={18} />,
    title: 'Join conversations on autopilot',
    points: [
      'Engagement Queue serves one post at a time with a pre-written comment',
      'AI comments reference your actual episode content — never generic',
      'Chrome extension auto-fills comments on Instagram and TikTok',
    ],
  },
  {
    id: 'network',
    label: 'Network',
    icon: <Users size={18} />,
    title: 'Build relationships that become listeners and guests',
    points: [
      'Track every creator you engage with and your relationship momentum',
      'Potential Guest tagging identifies who to invite on your show',
      'AI-generated outreach pitches personalized to each creator',
    ],
  },
  {
    id: 'promote',
    label: 'Promote',
    icon: <Mic size={18} />,
    title: 'Turn every episode into a growth engine',
    points: [
      'Promote Episode creates a signal from your episode\'s actual transcript',
      'Clip-to-Signal: paste a YouTube or Spotify link, we extract the topics',
      'Find conversations about your episode\'s themes across all platforms',
    ],
  },
  {
    id: 'analyze',
    label: 'Analyze',
    icon: <BarChart3 size={18} />,
    title: 'See what no podcast hosting platform shows you',
    points: [
      'Audience Insights: which platforms your listeners are on, what they talk about',
      'Episode Performance: which episodes sparked the most social engagement',
      'Podcast ratings and reviews from Podchaser, plus cross-promo opportunities',
    ],
  },
]

// ==========================================
// PRICING
// ==========================================
const TIERS = [
  {
    name: 'Starter',
    price: '29',
    yearlyPrice: '19',
    description: 'Perfect for new podcasters',
    features: ['3 Social Radar signals', 'Engagement Queue', 'AI comment generation', 'Network & guest discovery', '1,000 AI credits/mo'],
    planMonthly: 'plus_plan',
    planYearly: 'plus_plan_year',
  },
  {
    name: 'Growth',
    price: '59',
    yearlyPrice: '49',
    description: 'Scale your audience',
    features: ['10 Social Radar signals', 'Engagement Queue', 'AI comment generation', 'Audience discovery', '10,000 AI credits/mo', 'Advanced analytics'],
    highlighted: true,
    planMonthly: 'professional_plan',
    planYearly: 'professional_plan_year',
  },
  {
    name: 'Pro',
    price: '149',
    yearlyPrice: '129',
    description: 'For podcast networks',
    features: ['Unlimited signals', 'Unlimited AI credits', 'Audience discovery', 'Advanced analytics', 'Priority support'],
    planMonthly: 'business_plan',
    planYearly: 'business_plan_year',
  },
]

// ==========================================
// FAQ
// ==========================================
const FAQS = [
  {
    q: 'How is this different from a social media scheduling tool?',
    a: 'Scheduling tools help you post your own content. Nalapod helps you engage with other people\'s content — which is how podcasters actually grow. We find conversations in your niche, write authentic comments, and help you build real relationships that turn into listeners.',
  },
  {
    q: 'Will my comments sound like a bot?',
    a: 'No. Our AI generates comments based on your actual episode content, your personal style, and the specific post you\'re replying to. Every comment is editable before you post it. We also have guardrails that prevent generic, self-deprecating, or salesy language.',
  },
  {
    q: 'How long does it take to see results?',
    a: 'Most podcasters see their first new followers within the first week of consistent engagement. The key is daily engagement with the right people — which is exactly what our Engagement Queue is designed for. 10-15 minutes per day is enough.',
  },
  {
    q: 'Do I need to connect my podcast hosting platform?',
    a: 'No. During onboarding you just search for your podcast name and we find it automatically. We pull your episode data from public podcast directories — no API keys or hosting platform access needed.',
  },
  {
    q: 'What social platforms do you support?',
    a: 'Instagram, TikTok, YouTube, Reddit, Twitter, and Bluesky. Our Chrome extension supports auto-filling comments on Instagram and TikTok. For other platforms, we copy the comment to your clipboard.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. No contracts, no commitments. Cancel anytime from your account settings.',
  },
]

// ==========================================
// STATS
// ==========================================
const STATS = [
  { value: '72%', label: 'of podcast listeners discover shows through social media recommendations' },
  { value: '10 min', label: 'per day is all it takes to consistently grow with authentic engagement' },
  { value: '3x', label: 'more likely to subscribe when a podcaster engages with you personally' },
]

const ROTATING_WORDS = ['Conversation', 'Opportunity', 'Moment', 'Connection']

function RotatingWord(): JSX.Element {
  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'entering'>('visible')

  React.useEffect(() => {
    const interval = setInterval(() => {
      // Phase 1: current word slides down + fades out
      setPhase('exiting')
      setTimeout(() => {
        // Phase 2: swap word, new word starts above + fades in
        setIndex((prev) => (prev + 1) % ROTATING_WORDS.length)
        setPhase('entering')
        setTimeout(() => {
          // Phase 3: settle into place
          setPhase('visible')
        }, 50)
      }, 350)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: phase === 'exiting' ? 0 : 1,
        transform: phase === 'exiting'
          ? 'translateY(20px)'
          : phase === 'entering'
            ? 'translateY(-20px)'
            : 'translateY(0)',
        minWidth: { xs: '200px', md: '340px' },
      }}
    >
      {ROTATING_WORDS[index]}
    </Box>
  )
}

export default function PodcastLanding(): JSX.Element {
  const [activeTab, setActiveTab] = useState('discover')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly')
  const activeFeature = FEATURE_TABS.find((t) => t.id === activeTab) ?? FEATURE_TABS[0]

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0f172a' }}>
      {/* Nav */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 6 }, py: 2 }}>
        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '20px' }}>nalapod</Typography>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography component="a" href="#features" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', textDecoration: 'none', '&:hover': { color: 'white' }, display: { xs: 'none', md: 'block' } }}>Features</Typography>
          <Typography component="a" href="#pricing" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', textDecoration: 'none', '&:hover': { color: 'white' }, display: { xs: 'none', md: 'block' } }}>Pricing</Typography>
          <Typography component="a" href="#faq" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', textDecoration: 'none', '&:hover': { color: 'white' }, display: { xs: 'none', md: 'block' } }}>FAQ</Typography>
          <Button href="https://app.nalapod.com/dashboard" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 600, fontSize: '13px', '&:hover': { color: 'white' } }}>
            Log In
          </Button>
          <Button href="https://app.nalapod.com/subscription" sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '10px', textTransform: 'none', px: 2.5, fontWeight: 600, fontSize: '13px' }}>
            Start Free Trial
          </Button>
        </Box>
      </Box>

      {/* Hero */}
      <Container maxWidth="md" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, borderRadius: '20px', backgroundColor: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', mb: 3 }}>
          <Mic size={14} color="#a855f7" />
          <Typography sx={{ color: '#a855f7', fontSize: '13px', fontWeight: 600 }}>Built for podcasters</Typography>
        </Box>

        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: { xs: '32px', md: '56px' }, lineHeight: 1.15, mb: 2 }}>
          Never Miss the<br />
          <RotatingWord /><br />
          That Could Grow Your Podcast
        </Typography>

        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '16px', md: '20px' }, maxWidth: 600, mx: 'auto', mb: 4, lineHeight: 1.6 }}>
          Nalapod finds conversations about your podcast topics across social media and helps you engage authentically — on autopilot. More conversations. More listeners. Less time.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
          <Button href="https://app.nalapod.com/subscription" endIcon={<ArrowRight size={18} />} sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '14px', textTransform: 'none', px: 4, py: 1.5, fontWeight: 700, fontSize: '16px' }}>
            Start Free Trial
          </Button>
          <Button href="#features" sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '14px', textTransform: 'none', px: 4, py: 1.5, fontWeight: 600, fontSize: '16px', '&:hover': { borderColor: 'rgba(168,85,247,0.5)' } }}>
            See How It Works
          </Button>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
          No bots. 100% authentic engagement. Cancel anytime.
        </Typography>

        <HeroMock />
      </Container>

      {/* Social Proof Stats */}
      <Box sx={{ py: 5, backgroundColor: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 6 }, justifyContent: 'center', textAlign: 'center' }}>
            {STATS.map((stat) => (
              <Box key={stat.value} sx={{ flex: 1 }}>
                <Typography sx={{ color: '#a855f7', fontWeight: 800, fontSize: { xs: '28px', md: '36px' }, mb: 0.5 }}>{stat.value}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.5 }}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Feature Tabs */}
      <Box id="features" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, textAlign: 'center', mb: 1 }}>
            Everything you need to grow your podcast
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mb: 4, fontSize: '16px' }}>
            From finding conversations to building your network
          </Typography>

          {/* Tabs */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4, flexWrap: 'wrap' }}>
            {FEATURE_TABS.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                startIcon={tab.icon}
                sx={{
                  backgroundColor: activeTab === tab.id ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.03)',
                  border: activeTab === tab.id ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.1)',
                  color: activeTab === tab.id ? 'white' : 'rgba(255,255,255,0.5)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                  px: 2,
                  '&:hover': { backgroundColor: 'rgba(168,85,247,0.1)' },
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          {/* Active feature content */}
          <Box sx={{ p: { xs: 3, md: 4 }, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '20px', md: '24px' }, mb: 2 }}>
              {activeFeature.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activeFeature.points.map((point, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle size={18} color="#a855f7" style={{ marginTop: 2, flexShrink: 0 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>

            {/* Feature-specific mock UI */}
            {activeTab === 'discover' && <DiscoverMock />}
            {activeTab === 'engage' && <EngageMock />}
            {activeTab === 'network' && <NetworkMock />}
            {activeTab === 'promote' && <PromoteMock />}
            {activeTab === 'analyze' && <AnalyzeMock />}
          </Box>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="md">
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, textAlign: 'center', mb: 1 }}>
            How it works
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mb: 5, fontSize: '16px' }}>
            Set up in 2 minutes. See results in your first week.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1, p: 3, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: 'white' }}><Mic size={20} /></Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '18px', mb: 1 }}>Connect your podcast</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6 }}>Search for your show by name. We auto-create engagement signals from your podcast topics, latest episodes, and mentions.</Typography>
              <StepOneMock />
            </Box>
            <Box sx={{ flex: 1, p: 3, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: 'white' }}><Zap size={20} /></Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '18px', mb: 1 }}>Engage daily (10 min)</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6 }}>Open your queue each morning. AI has found the best conversations and written the first draft of every comment. Review, tweak, reply.</Typography>
              <StepTwoMock />
            </Box>
            <Box sx={{ flex: 1, p: 3, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: 'white' }}><TrendingUp size={20} /></Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '18px', mb: 1 }}>Watch your audience grow</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6 }}>Track your network, discover potential guests, find their audience, and see which episodes drive the most social engagement.</Typography>
              <StepThreeMock />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing */}
      <Box id="pricing" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, textAlign: 'center', mb: 1 }}>
            Simple, transparent pricing
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mb: 2, fontSize: '16px' }}>
            Start free. Upgrade when you&apos;re ready.
          </Typography>

          {/* Billing toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
            <Button onClick={() => setBillingCycle('monthly')} sx={{ color: billingCycle === 'monthly' ? 'white' : 'rgba(255,255,255,0.4)', textTransform: 'none', fontWeight: 600 }}>Monthly</Button>
            <Button onClick={() => setBillingCycle('yearly')} sx={{ color: billingCycle === 'yearly' ? 'white' : 'rgba(255,255,255,0.4)', textTransform: 'none', fontWeight: 600 }}>
              Yearly <Box component="span" sx={{ ml: 0.5, fontSize: '11px', color: '#22c55e' }}>Save 30%</Box>
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'center' }}>
            {TIERS.map((tier) => (
              <Box
                key={tier.name}
                sx={{
                  flex: 1,
                  maxWidth: 340,
                  p: 3,
                  backgroundColor: tier.highlighted ? 'rgba(168,85,247,0.08)' : 'rgba(255,255,255,0.03)',
                  border: tier.highlighted ? '1px solid rgba(168,85,247,0.3)' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '16px',
                  position: 'relative',
                }}
              >
                {tier.highlighted && (
                  <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.25, borderRadius: '12px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)' }}>
                    <Star size={12} color="white" />
                    <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>Most Popular</Typography>
                  </Box>
                )}
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '20px', mb: 0.5 }}>{tier.name}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', mb: 2 }}>{tier.description}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                  <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '40px' }}>
                    ${billingCycle === 'yearly' ? tier.yearlyPrice : tier.price}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', ml: 0.5 }}>/mo</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  {tier.features.map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={14} color="#a855f7" />
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  href={`https://app.nalapod.com/subscription?plan=${billingCycle === 'yearly' ? String(tier.planYearly) : String(tier.planMonthly)}`}
                  fullWidth
                  sx={{
                    background: tier.highlighted ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)' : 'rgba(255,255,255,0.05)',
                    color: 'white',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.2,
                    border: tier.highlighted ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  Try For Free
                </Button>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ */}
      <Box id="faq" sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="sm">
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, textAlign: 'center', mb: 4 }}>
            Frequently asked questions
          </Typography>

          {FAQS.map((faq, i) => (
            <Accordion
              key={i}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={18} color="rgba(255,255,255,0.5)" />}>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '15px' }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.6 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, mb: 2 }}>
          Stop scrolling. Start growing.
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px', mb: 4, lineHeight: 1.6 }}>
          Every day you spend not engaging is a day your competitors are building relationships with your future listeners.
        </Typography>
        <Button href="https://app.nalapod.com/subscription" endIcon={<ArrowRight size={18} />} sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '14px', textTransform: 'none', px: 5, py: 1.5, fontWeight: 700, fontSize: '16px' }}>
          Start Your Free Trial
        </Button>
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', mt: 2 }}>
          No credit card required. Cancel anytime.
        </Typography>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Typography component="a" href="/podcast-analyzer" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textDecoration: 'none', '&:hover': { color: 'white' } }}>Free Podcast Analyzer</Typography>
            <Typography component="a" href="/privacy" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textDecoration: 'none', '&:hover': { color: 'white' } }}>Privacy</Typography>
            <Typography component="a" href="/termsandconditions" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textDecoration: 'none', '&:hover': { color: 'white' } }}>Terms</Typography>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px' }}>
            Nalapod — Podcast audience growth through authentic social engagement
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}
